import { buildWarmHandoffSummary, WarmHandoffAnswers } from '@bariendo/lib/patient-access-warm-handoff-note';
import { PAC_WARM_HANDOFF_SCRIPT, NOT_READY_TO_COMMIT } from '@bariendo/lib/patient-access-warm-handoff-script';
import { PacAction, PacWidget } from '@bariendo/lib/patient-access-script';
import { PAC_WARM_HANDOFF_ANSWERS_PAYLOAD_ID } from '@bariendo/lib/constants';
import { Communication } from '@medplum/fhirtypes';
import { useCallback, useState } from 'react';
import { PatientAccessCallCard } from './PatientAccessCallCard';
import { ProcedureDateGrid } from './ProcedureDateGrid';
import { usePatientAccessCallDraft } from './usePatientAccessCallDraft';
import { useProcedureBlockDates } from './useProcedureBlockDates';
import { useProcedureDateAnswer } from './useProcedureDateAnswer';
import { openPaymentOrder } from './openPaymentOrder';
import { summarizeWarmHandoffBeat } from './warmHandoffSummary';

/** The widget id the procedure-date grid answers under on this script. */
const PROCEDURE_DATE_ID = 't_date';

interface WarmHandoffCardProps {
  note: Communication;
  answers: WarmHandoffAnswers;
  /**
   * Opens the payment-order drawer. Absent when the rep cannot reach payments —
   * outside `PAYMENTS_ROLES`, or with the treatment-payments flag off.
   */
  onOpenPaymentOrder?: () => void;
  /**
   * A note already saved to the chart. Expanding it shows the worked script as a
   * record of the call; it is not an editor.
   */
  readOnly?: boolean;
  onSaved?: () => void;
}

/**
 * The warm hand-off call: the PAC turns a completed NP consult into an agreed
 * procedure date and a payment.
 *
 * Owns what is specific to this script — the procedure-date grid and the payment
 * pivot — and renders the shared card spine.
 *
 * Payment is an order, never a card entry. The pivot opens the payment-order
 * drawer; card numbers are never read aloud, typed into Orbit or recorded here.
 * The card records which option the patient chose and whether payment completed.
 */
export function WarmHandoffCard({
  note,
  answers: initialAnswers,
  onOpenPaymentOrder,
  readOnly = false,
  onSaved,
}: WarmHandoffCardProps) {
  const { answers, updateAnswers, setValue, saveToChart, isSaving, saveError } =
    usePatientAccessCallDraft<WarmHandoffAnswers>({
      note,
      initialAnswers,
      answersPayloadId: PAC_WARM_HANDOFF_ANSWERS_PAYLOAD_ID,
      buildSummary: buildWarmHandoffSummary,
      onSaved,
    });

  /** Availability is only read while the timing beat is on screen. */
  const [openBeat, setOpenBeat] = useState<string | null>(null);
  const dates = useProcedureBlockDates(openBeat === 'timing');

  const declined = answers.values[PROCEDURE_DATE_ID] === NOT_READY_TO_COMMIT;
  const { handleSelectDate, handleDecline } = useProcedureDateAnswer(updateAnswers, PROCEDURE_DATE_ID);

  const handleAction = useCallback(
    (action: PacAction) => {
      if (action === 'openPaymentOrder') openPaymentOrder(onOpenPaymentOrder);
    },
    [onOpenPaymentOrder]
  );

  const renderCardWidget = useCallback(
    (widget: PacWidget) => {
      if (widget.kind !== 'procedureDateGrid') return null;

      return (
        <ProcedureDateGrid
          options={dates.options}
          loading={dates.loading}
          error={dates.error}
          selected={answers.procedureDate}
          declined={declined}
          onSelect={handleSelectDate}
          onDecline={handleDecline}
          disabled={readOnly}
        />
      );
    },
    [dates, answers.procedureDate, declined, handleSelectDate, handleDecline, readOnly]
  );

  return (
    <PatientAccessCallCard<WarmHandoffAnswers>
      script={PAC_WARM_HANDOFF_SCRIPT}
      answers={answers}
      setValue={setValue}
      summarizeBeat={summarizeWarmHandoffBeat}
      onAction={handleAction}
      renderCardWidget={renderCardWidget}
      readOnly={readOnly}
      isSaving={isSaving}
      saveError={saveError}
      onSave={() => void saveToChart()}
      onOpenBeatChange={setOpenBeat}
    />
  );
}
